import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI, visitsAPI, timesheetsAPI, exportAPI } from '../lib/api';
import WeatherStation from '../components/WeatherStation';
import { formatDate, formatTime, formatHours } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Users,
  Clock,
  Calendar,
  CheckCircle,
  AlertCircle,
  FileText,
  TrendingUp,
  Car,
  Download,
  Loader2,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

export default function ManagerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      // Fetch stats and activity independently to handle partial failures
      let statsData = null;
      let activityData = [];
      
      try {
        const statsRes = await dashboardAPI.getStats();
        statsData = statsRes.data;
      } catch (statsErr) {
        console.error('Failed to fetch stats:', statsErr);
        // Don't show toast for stats failure, just use fallback
      }
      
      try {
        const activityRes = await dashboardAPI.getRecentActivity();
        activityData = activityRes.data || [];
      } catch (activityErr) {
        console.error('Failed to fetch activity:', activityErr);
        // Don't show toast for activity failure, just use empty array
      }
      
      setStats(statsData);
      setActivity(activityData);
    } catch (err) {
      console.error('Unexpected error loading dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await exportAPI.timesheetsCSV();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `timesheets_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Export downloaded');
    } catch (err) {
      toast.error('Failed to export timesheets');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-secondary" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="manager-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Dashboard</h1>
          <div className="mt-4 w-full"><WeatherStation /></div>
          <p className="text-muted-foreground">
            {formatDate(new Date().toISOString())}
          </p>
        </div>
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <Button 
            onClick={handleExportCSV} 
            disabled={exporting}
            className="bg-secondary hover:bg-secondary/90"
            data-testid="export-csv-btn"
          >
            {exporting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Export to Xero
          </Button>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card" data-testid="stat-workers-on-shift">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Users className="w-4 h-4 text-secondary" />
            </div>
          </div>
          <p className="stat-value">{stats?.staff_on_shift || stats?.workers_on_shift || 0}</p>
          <p className="stat-label">Staff On Shift</p>
        </Card>

        <Card className="stat-card" data-testid="stat-visits-in-progress">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
              <Activity className="w-4 h-4 text-amber-600" />
            </div>
          </div>
          <p className="stat-value">{stats?.visits_in_progress || 0}</p>
          <p className="stat-label">Visits In Progress</p>
        </Card>

        <Card className="stat-card" data-testid="stat-visits-completed">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
            </div>
          </div>
          <p className="stat-value">{stats?.visits_completed_today || 0}/{stats?.visits_scheduled_today || 0}</p>
          <p className="stat-label">Visits Today</p>
        </Card>

        <Card className="stat-card" data-testid="stat-pending-approvals">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-4 h-4 text-blue-600" />
            </div>
          </div>
          <p className="stat-value">{stats?.pending_approvals || 0}</p>
          <p className="stat-label">Pending Approvals</p>
        </Card>
      </div>

      {/* Weekly Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card" data-testid="stat-week-service-hours">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-secondary" />
            </div>
            <TrendingUp className="w-4 h-4 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold">{formatHours(stats?.week_service_hours || 0)}</p>
          <p className="text-sm text-muted-foreground">Service Hours This Week</p>
        </Card>

        <Card className="stat-card" data-testid="stat-week-travel-hours">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Car className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <p className="text-3xl font-bold">{formatHours(stats?.week_travel_hours || 0)}</p>
          <p className="text-sm text-muted-foreground">Travel Hours This Week</p>
        </Card>

        <Card className="stat-card" data-testid="stat-week-visits">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
          <p className="text-3xl font-bold">{stats?.week_visits_completed || 0}</p>
          <p className="text-sm text-muted-foreground">Visits Completed This Week</p>
        </Card>
      </div>

      {/* Alerts */}
      {stats?.follow_up_notes > 0 && (
        <Card className="border-amber-200 bg-amber-50" data-testid="follow-up-alert">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <span className="font-medium text-amber-900">
                {stats.follow_up_notes} visit notes require follow-up
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Activity */}
      <Card data-testid="recent-activity-card">
        <CardHeader>
          <CardTitle className="text-lg font-heading">Recent Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {activity.length > 0 ? (
            <div className="space-y-3">
              {activity.map((item, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between py-2 border-b border-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${
                      item.type === 'clock_in' ? 'bg-emerald-500' : 'bg-red-500'
                    }`} />
                    <div>
                      <p className="font-medium text-sm">{item.staff_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {item.type === 'clock_in' ? 'Clocked in at' : 'Clocked out from'} {item.client_name}
                      </p>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {formatTime(item.time)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-4">No recent activity</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
